import * as Exit from "effect/Exit";
import * as Schema from "effect/Schema";

import { CleoProjectFile, CLEO_PROJECT_FILE_SCHEMA_URL } from "@cleo/contracts";

import { fromLenientJson } from "./schemaJson.ts";

/**
 * Codec between the raw `cleo.json` file contents (lenient JSONC string) and the
 * decoded {@link CleoProjectFile}.
 */
export const CleoProjectFileFromJson = fromLenientJson(CleoProjectFile);

const decodeCleoProjectFile = Schema.decodeExit(CleoProjectFileFromJson);

/**
 * Decode raw `cleo.json` contents, treating invalid or malformed files as
 * absent. Clients use this to read optional defaults (scripts, thread env
 * mode) without surfacing decode errors to the user.
 */
export function parseCleoProjectFile(contents: string): CleoProjectFile | null {
  const decoded = decodeCleoProjectFile(contents);
  return Exit.isSuccess(decoded) ? decoded.value : null;
}

/**
 * Build the publishable JSON Schema document for `cleo.json` (draft 2020-12).
 *
 * Published at {@link CLEO_PROJECT_FILE_SCHEMA_URL} so editors get LSP support
 * via a `$schema` reference. The marketing route renders this same document.
 */
export function buildCleoProjectFileJsonSchema(): Record<string, unknown> {
  const document = Schema.toJsonSchemaDocument(CleoProjectFile);
  const jsonSchema: Record<string, unknown> = {
    $schema: "https://json-schema.org/draft/2020-12/schema",
    $id: CLEO_PROJECT_FILE_SCHEMA_URL,
    ...document.schema,
  };
  if (document.definitions && Object.keys(document.definitions).length > 0) {
    jsonSchema.$defs = document.definitions;
  }
  return jsonSchema;
}
